/**
 * Minimal, dependency-free ZIP archive builder for the browser.
 *
 * Produces a standard .zip Blob. Each entry is compressed with raw DEFLATE
 * (method 8) using the native CompressionStream API when available, and falls
 * back to STORE (method 0) when compression is unavailable or doesn't help.
 *
 * Limitations (intentional — none are hit by our JSON exports): no ZIP64, so
 * individual entries and the archive must stay under 4 GB and below 65,535
 * entries.
 */

export interface ZipEntry {
  /** Path inside the archive, e.g. "my_document.json". */
  name: string;
  data: Uint8Array | string;
}

// CRC-32 (IEEE 802.3) with a lazily-built lookup table.
let crcTable: Uint32Array | null = null;
const getCrcTable = (): Uint32Array => {
  if (crcTable) return crcTable;
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  crcTable = table;
  return table;
};

const crc32 = (bytes: Uint8Array): number => {
  const table = getCrcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = table[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
};

// Raw DEFLATE via the native CompressionStream. Returns null when the API or
// the "deflate-raw" format is unavailable (older browsers), so callers can
// fall back to STORE.
const deflateRaw = async (
  bytes: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer> | null> => {
  if (typeof CompressionStream === "undefined") return null;
  try {
    const cs = new CompressionStream("deflate-raw");
    const compressedStream = new Response(bytes).body!.pipeThrough(cs);
    const buffer = await new Response(compressedStream).arrayBuffer();
    return new Uint8Array(buffer);
  } catch {
    return null;
  }
};

const encoder = new TextEncoder();

const u16 = (n: number): number[] => [n & 0xff, (n >>> 8) & 0xff];
const u32 = (n: number): number[] => [
  n & 0xff,
  (n >>> 8) & 0xff,
  (n >>> 16) & 0xff,
  (n >>> 24) & 0xff,
];

export const createZipBlob = async (entries: ZipEntry[]): Promise<Blob> => {
  // Flag bit 11 marks filenames as UTF-8 (per APPNOTE 6.3.2).
  const UTF8_FLAG = 0x0800;

  const fileParts: BlobPart[] = [];
  const centralDirectory: number[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name);
    const rawData: Uint8Array<ArrayBuffer> =
      typeof entry.data === "string"
        ? encoder.encode(entry.data)
        : (entry.data as Uint8Array<ArrayBuffer>);
    const crc = crc32(rawData);
    const uncompressedSize = rawData.length;

    let method = 0;
    let payload: Uint8Array<ArrayBuffer> = rawData;
    const compressed = await deflateRaw(rawData);
    if (compressed && compressed.length < uncompressedSize) {
      method = 8;
      payload = compressed;
    }
    const compressedSize = payload.length;

    // Local file header (30 fixed bytes + filename).
    const localHeader = [
      ...u32(0x04034b50),
      ...u16(20), // version needed to extract
      ...u16(UTF8_FLAG),
      ...u16(method),
      ...u16(0), // mod time
      ...u16(0), // mod date
      ...u32(crc),
      ...u32(compressedSize),
      ...u32(uncompressedSize),
      ...u16(nameBytes.length),
      ...u16(0), // extra field length
    ];
    fileParts.push(new Uint8Array(localHeader), nameBytes, payload);

    // Central directory header (mirrors the local header + offset).
    centralDirectory.push(
      ...u32(0x02014b50),
      ...u16(20), // version made by
      ...u16(20), // version needed to extract
      ...u16(UTF8_FLAG),
      ...u16(method),
      ...u16(0), // mod time
      ...u16(0), // mod date
      ...u32(crc),
      ...u32(compressedSize),
      ...u32(uncompressedSize),
      ...u16(nameBytes.length),
      ...u16(0), // extra field length
      ...u16(0), // file comment length
      ...u16(0), // disk number start
      ...u16(0), // internal attributes
      ...u32(0), // external attributes
      ...u32(offset), // relative offset of local header
    );
    for (const b of nameBytes) centralDirectory.push(b);

    offset += localHeader.length + nameBytes.length + compressedSize;
  }

  const centralBytes = new Uint8Array(centralDirectory);
  const eocd = [
    ...u32(0x06054b50),
    ...u16(0), // number of this disk
    ...u16(0), // disk where central directory starts
    ...u16(entries.length), // central directory records on this disk
    ...u16(entries.length), // total central directory records
    ...u32(centralBytes.length),
    ...u32(offset), // offset of start of central directory
    ...u16(0), // comment length
  ];

  return new Blob([...fileParts, centralBytes, new Uint8Array(eocd)], {
    type: "application/zip",
  });
};
